import { TrendingDown, TrendingUp, Calendar, Target, AlertTriangle } from 'lucide-react';

interface ComparisonData {
  base: {
    treasury_min: number;
    frd: number;
    rupture_date: Date | null;
    runway_days: number | null;
    security_threshold: number;
  };
  scenario: {
    treasury_min: number;
    frd: number;
    rupture_date: Date | null;
    runway_days: number | null;
    security_threshold: number;
  };
}

interface ScenarioComparisonProps {
  data: ComparisonData;
  scenarioName: string;
}

export function ScenarioComparison({ data, scenarioName }: ScenarioComparisonProps) {
  const treasuryDelta = data.scenario.treasury_min - data.base.treasury_min;
  const frdDelta = data.scenario.frd - data.base.frd;

  let runwayDelta: number | null = null;
  if (data.base.runway_days !== null && data.scenario.runway_days !== null) {
    runwayDelta = data.scenario.runway_days - data.base.runway_days;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'Aucune';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const DeltaIndicator = ({ value, inverse = false }: { value: number; inverse?: boolean }) => {
    const isPositive = inverse ? value < 0 : value > 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';

    return (
      <div className={`flex items-center space-x-1 ${colorClass}`}>
        <Icon className="h-4 w-4" />
        <span className="font-semibold">
          {value > 0 ? '+' : ''}{formatCurrency(value)}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Comparaison : Base vs {scenarioName}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <Target className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium text-blue-900">Trésorerie minimale</h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Base :</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(data.base.treasury_min)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Scénario :</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(data.scenario.treasury_min)}
                </span>
              </div>
              <div className="pt-2 border-t border-blue-300">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Impact :</span>
                  <DeltaIndicator value={treasuryDelta} />
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <h4 className="font-medium text-purple-900">FRD (Fonds de Roulement Dynamique)</h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Base :</span>
                <span className={`font-semibold ${
                  data.base.frd >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(data.base.frd)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Scénario :</span>
                <span className={`font-semibold ${
                  data.scenario.frd >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(data.scenario.frd)}
                </span>
              </div>
              <div className="pt-2 border-t border-purple-300">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Impact :</span>
                  <DeltaIndicator value={frdDelta} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <Calendar className="h-5 w-5 text-orange-600" />
              <h4 className="font-medium text-orange-900">Date de rupture</h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Base :</span>
                <span className="font-semibold text-gray-900">
                  {formatDate(data.base.rupture_date)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Scénario :</span>
                <span className="font-semibold text-gray-900">
                  {formatDate(data.scenario.rupture_date)}
                </span>
              </div>
              {data.base.rupture_date && data.scenario.rupture_date && (
                <div className="pt-2 border-t border-orange-300">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Décalage :</span>
                    <span className={`font-semibold ${
                      (new Date(data.scenario.rupture_date).getTime() - new Date(data.base.rupture_date).getTime()) < 0
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}>
                      {Math.round((new Date(data.scenario.rupture_date).getTime() - new Date(data.base.rupture_date).getTime()) / (1000 * 60 * 60 * 24))} jours
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h4 className="font-medium text-red-900">Runway (jours restants)</h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Base :</span>
                <span className="font-semibold text-gray-900">
                  {data.base.runway_days !== null ? `${data.base.runway_days} jours` : 'Infini'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Scénario :</span>
                <span className="font-semibold text-gray-900">
                  {data.scenario.runway_days !== null ? `${data.scenario.runway_days} jours` : 'Infini'}
                </span>
              </div>
              {runwayDelta !== null && (
                <div className="pt-2 border-t border-red-300">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Impact :</span>
                    <span className={`font-semibold ${
                      runwayDelta < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {runwayDelta > 0 ? '+' : ''}{runwayDelta} jours
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <div className="h-2 w-2 rounded-full bg-blue-600"></div>
          <span className="text-sm font-medium text-gray-700">Seuil de sécurité</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">
          {formatCurrency(data.scenario.security_threshold)}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Basé sur 3 mois de charges fixes
        </p>
      </div>

      {data.scenario.frd < 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <div className="font-semibold text-red-900 mb-1">Alerte critique</div>
              <p className="text-sm text-red-700">
                Le FRD est négatif dans ce scénario. La trésorerie minimale est inférieure au seuil de sécurité.
                Actions correctives requises immédiatement.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
